import countries from "i18n-iso-countries";
import { createIntl, createIntlCache, MessageDescriptor } from "react-intl";
import messagesEn from "../translations/en.json";
import messagesEs from "../translations/es.json";
import messagesFr from "../translations/fr.json";
import { FormatNumberOptions } from "@formatjs/intl/src/types";

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/es.json"));
countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));

export type AllowedLocales = "es" | "en" | "fr";

// Our API does not recognize Kosovo as a country ¯\_(ツ)_/¯
export const countryCodes = Object.keys(countries.getAlpha2Codes()).filter(it => it !== "XK");

function sortCountries(list: { label: string; value: string }[]) {
  return list
    .map(it => ({ ...it, label: it.label.substring(5, it.label.length) }))
    .sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
}

const enList = countryCodes.map(it => ({
  label: `[${it}] ${countries.getName(it, "en", { select: "official" })}`,
  value: it,
}));

const esList = countryCodes.map(it => ({
  label: `[${it}] ${countries.getName(it, "es", { select: "official" })}`,
  value: it,
}));

const frList = countryCodes.map(it => ({
  label: `[${it}] ${countries.getName(it, "fr", { select: "official" })}`,
  value: it,
}));

export function getLocaleList(): string[] {
  return [...countries.getSupportedLanguages()].sort().reverse();
}
export function getTranslatedCountryList(locale: AllowedLocales) {
  switch (locale) {
    case "fr":
      return sortCountries(frList);
    case "en":
      return sortCountries(enList);
    case "es":
    default:
      return sortCountries(esList);
  }
}

export function getTranslatedCountry(countryCode: string, locale?: string) {
  return countries.getName(countryCode, locale || "es");
}

const cache = createIntlCache();
const intlEs = createIntl(
  {
    locale: "es-ES",
    messages: messagesEs,
  },
  cache
);
const intlEn = createIntl(
  {
    locale: "en-US",
    messages: messagesEn,
  },
  cache
);
const intlFr = createIntl(
  {
    locale: "fr-FR",
    messages: messagesFr,
  },
  cache
);

// This util lets you use react-intl outside a component
export const formatMessage = (message: MessageDescriptor, locale: string) => {
  switch (locale) {
    case "es-ES":
      return intlEs.formatMessage(message);
    case "fr-FR":
      return intlFr.formatMessage(message);
    case "en-US":
    default:
      return intlEn.formatMessage(message);
  }
};

// This util lets you use react-intl outside a component
export const formatNumber = (value: number, locale: string, opts?: FormatNumberOptions) => {
  switch (locale) {
    case "fr-FR":
      return intlFr.formatNumber(value, opts);
    case "es-ES":
      return intlEs.formatNumber(value, opts);
    case "en-US":
    default:
      return intlEn.formatNumber(value, opts);
  }
};
